<template>
  <div id="login">

    <!-- SPINNER -->
    <transition name="fade">
      <div id="map-spinner" v-if="performingRequest">
        <RingLoader
          :color="'#61539b'"
        ></RingLoader>
      </div><!-- /#map-spinner -->
    </transition>

    <div class="login-form">
      <img src="../../assets/Every Farm_alt_v2_text.png" class="logo">

      <!-- Farm-invitation context. Shown when the user arrived here from an
           /invite/... link — the single most confusing moment for invitees
           without an account, who otherwise read this page as a dead end. -->
      <div v-if="cameFromInvite" class="invite-hint">
        <p><strong>You've been invited to a farm on Every.Farm.</strong></p>
        <p v-if="showLoginForm">
          Have an account? Log in below and your invitation will open.
          <br>New here? Click <em>Create an Account</em> and sign up
          <strong>using the email address that received the invitation</strong> —
          it will be added to the farm automatically.
        </p>
        <p v-else>
          Sign up <strong>using the email address that received the
          invitation</strong> and you'll be added to the farm automatically.
        </p>
      </div>

      <div :class="{ 'signup-form': !showLoginForm && !showForgotPassword }">

        <!-- Login Form -->
        <form v-if="showLoginForm" @submit.prevent>
          <div class="form-group">
            <label for="email1">Email</label>
            <input v-model.trim="loginForm.email" type="text" placeholder="you@email.com" id="email1" class="form-control" />
          </div><!-- /.form-group -->
          <div class="form-group">
            <label for="password1">Password</label>
            <input v-model.trim="loginForm.password" type="password" placeholder="******" id="password1" class="form-control" />
          </div><!-- /.form-group -->
          <button @click="login" class="btn btn-primary">Log In</button>
          <div class="extras">
            <button class="btn btn-sm btn-secondary" @click="togglePasswordReset">Forgot Password</button>
          </div><!-- /.extras -->
          <div class="signup-prompt">
            <p>Don't have an account? Securely sign up below:</p>
            <button class="btn btn-secondary" @click="toggleForm">Create an Account</button>
          </div>
        </form>

        <!-- Signup Form -->
        <form v-if="!showLoginForm && !showForgotPassword" @submit.prevent>
          <h2>Create an Account</h2>
          <div class="form-group">
            <label for="email2">Email</label>
            <input v-model.trim="signupForm.email" type="text" placeholder="you@email.com" id="email2" class="form-control" />
          </div><!-- /.form-group -->
          <div class="form-group">
            <label for="password2">Password</label>
            <input v-model.trim="signupForm.password" type="password" placeholder="min 6 characters" id="password2" class="form-control" />
          </div><!-- /.form-group -->
          <button @click="signup" class="btn btn-primary">Sign Up</button>
          <div class="extras">
            <button class="btn btn-sm btn-secondary" @click="toggleForm">Back to Log In</button>
          </div><!-- /.extras -->
        </form>

        <!-- Forgot Password -->
        <form v-if="showForgotPassword" @submit.prevent class="password-reset">
          <div v-if="!passwordResetSuccess">
            <h2>Reset Password</h2>
            <p>We will send you an email to reset your password</p>
            <div class="form-group">
              <label for="email3">Email</label>
              <input v-model.trim="passwordForm.email" type="text" placeholder="you@email.com" id="email3" class="form-control" />
            </div><!-- /.form-control -->
            <button @click="resetPassword" class="btn btn-primary">Submit</button>
            <div class="extras">
              <button @click="togglePasswordReset" class="btn btn-sm btn-secondary">Back to Log In</button>
            </div><!-- /.extras -->
          </div><!-- /v-if=!passwordResetSuccess -->
          <div v-else>
            <h2>Email Sent</h2>
            <p>check your email for a link to reset your password</p>
            <button @click="togglePasswordReset" class="btn btn-primary">Back to login</button>
          </div><!-- /v-else -->
        </form>

        <!-- Error Message -->
        <transition name="fade">
          <div v-if="errorMsg !== ''" class="error-msg">
            <p>{{ errorMsg }}</p>
          </div><!-- error-msg -->
        </transition>

      </div><!-- /.signup-form -->
    </div><!-- /.container -->
  </div><!-- /#login -->
</template>

<script>
import RingLoader from 'vue-spinner/src/GridLoader'
const fb = require('../../firebaseConfig.js')

export default {
  components: { RingLoader },
  data () {
    return {
      loginForm: {
        email: '',
        password: ''
      },
      signupForm: {
        email: '',
        password: ''
      },
      passwordForm: {
        email: ''
      },
      showLoginForm: true,
      showForgotPassword: false,
      passwordResetSuccess: false,
      performingRequest: false,
      spinnerColor: '#61539b',
      spinnerSize: '50px',
      errorMsg: ''
    }
  },
  computed: {
    // True when the router bounced the user here from an /invite/... link
    // (the ?redirect= query the router guard preserves).
    cameFromInvite () {
      const r = this.$route.query && this.$route.query.redirect
      return typeof r === 'string' && r.indexOf('/invite/') === 0
    }
  },
  created () {
    if (this.$route.params.mode === 'create') {
      this.showLoginForm = false
    }
    if (this.$route.params.mode === 'reset') {
      this.showLoginForm = false
      this.showForgotPassword = true
    }
  },
  methods: {
    // After a successful sign-in or sign-up, honor a ?redirect=…
    // query param if the router guard sent the user here from a
    // protected route. Whitelist to internal paths only so a crafted
    // /login?redirect=https://evil.example.com can't bounce users
    // off-site.
    postLoginDestination () {
      const redirect = this.$route && this.$route.query && this.$route.query.redirect
      if (typeof redirect === 'string' && redirect.startsWith('/') && !redirect.startsWith('//')) {
        return redirect
      }
      return '/farm'
    },
    toggleForm () {
      this.errorMsg = ''
      this.showLoginForm = !this.showLoginForm
    },
    togglePasswordReset () {
      if (this.showForgotPassword) {
        this.showLoginForm = true
        this.showForgotPassword = false
        this.passwordResetSuccess = false
      } else {
        this.showLoginForm = false
        this.showForgotPassword = true
      }
    },
    login () {
      this.performingRequest = true
      fb.auth.signInWithEmailAndPassword(this.loginForm.email, this.loginForm.password).then(user => {
        this.$store.commit('setCurrentUser', user.user)
        this.$store.dispatch('fetchUserProfile')
        this.performingRequest = false
        this.$router.push(this.postLoginDestination())
      }).catch(err => {
        console.log(err)
        this.performingRequest = false
        this.errorMsg = err.message
      })
    },
    signup () {
      this.performingRequest = true
      fb.auth.createUserWithEmailAndPassword(this.signupForm.email, this.signupForm.password).then(user => {
        this.$store.commit('setCurrentUser', user.user)
        // create user obj
        fb.usersCollection.doc(user.user.uid).set({
          createdOn: new Date(),
          email: this.signupForm.email,
          emailNotifications: true,
          emailUpdates: true,
          measurementSystem: 'Imperial'
        }).then(() => {
          this.$store.dispatch('fetchUserProfile')
          this.performingRequest = false
          this.$router.push(this.postLoginDestination())
        }).catch(err => {
          console.log(err)
          this.performingRequest = false
          this.errorMsg = err.message
        })
      }).catch(err => {
        console.log(err)
        this.performingRequest = false
        this.errorMsg = err.message
      })
    },
    resetPassword () {
      this.performingRequest = true
      fb.auth.sendPasswordResetEmail(this.passwordForm.email).then(() => {
        this.performingRequest = false
        this.passwordResetSuccess = true
        this.passwordForm.email = ''
      }).catch(err => {
        console.log(err)
        this.performingRequest = false
        this.errorMsg = err.message
      })
    }
  }
}
</script>

<style scoped>
.invite-hint {
  background: rgba(6, 103, 57, 0.08);
  border: 1px solid rgba(6, 103, 57, 0.35);
  border-radius: 6px;
  padding: 12px 14px;
  margin-bottom: 16px;
  font-size: 0.9rem;
  text-align: left;
}
.invite-hint p { margin: 0 0 6px; }
.invite-hint p:last-child { margin-bottom: 0; }
#map-spinner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 5;
  background-color: rgba(255,255,255,0.5);
}

.v-spinner {
  position: absolute;
  left: calc(50% - 25px);
  top: calc(50vh - 50px);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.photo-credit {
  position: absolute;
  bottom: 0;
  right: 10px;
  color: white;
}
.photo-credit a {
  color: #eee;
}

.signup-prompt {
  margin-top: 1.5rem;
  text-align: center;
}

.signup-prompt p {
  margin-bottom: 0.5rem;
}
</style>
